/**
 * "Connect MarketMapr to Claude" as a picture walkthrough: one step at a
 * time, a screenshot each, Back / Next (arrow keys too). Opened from
 * Settings → Integrations. With the connector URL (just after creating a
 * key) the copy and paste steps carry a Copy button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CLAUDE_CONNECT_PARTS, CLAUDE_CONNECT_STEPS } from './claudeConnectSteps';

interface Props {
  onClose: () => void;
  connectorUrl?: string | null;
}

const ClaudeConnectGuide: React.FC<Props> = ({ onClose, connectorUrl }) => {
  const [i, setI] = useState(0);
  const [copied, setCopied] = useState(false);
  const steps = CLAUDE_CONNECT_STEPS;
  const s = steps[i];
  const last = i === steps.length - 1;

  const go = useCallback((d: number) => setI((n) => Math.max(0, Math.min(steps.length - 1, n + d))), [steps.length]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);
  useEffect(() => { setCopied(false); }, [i]);

  const copyUrl = async () => {
    if (!connectorUrl) return;
    try { await navigator.clipboard.writeText(connectorUrl); setCopied(true); } catch { /* the URL is shown in the panel to select */ }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-gray-900/60 flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="ccg-title" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
              Part {s.part} of 3 · {CLAUDE_CONNECT_PARTS[s.part]}
            </p>
            <h2 id="ccg-title" className="text-base font-semibold text-gray-900">Connect MarketMapr to Claude</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1" aria-label="Close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-blue-600 tabular-nums">{i + 1}.</span>
            <h3 className="text-lg font-semibold text-gray-900">{s.title}</h3>
          </div>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed max-w-[65ch]">{s.body}</p>
          {s.copyUrl && connectorUrl && (
            <button onClick={copyUrl} className="mt-3 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
              {copied ? 'Copied' : 'Copy connector URL'}
            </button>
          )}
          {s.image ? (
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-2 sm:p-3 flex justify-center">
              <img src={s.image} alt={s.alt || s.title} className="max-w-full max-h-[52vh] object-contain rounded-lg shadow-sm" />
            </div>
          ) : (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-900">
              That’s it: MarketMapr is connected. You only do this once, and it works in every chat.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100">
          <button onClick={() => go(-1)} disabled={i === 0} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30">← Back</button>
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {steps.map((st, n) => (
              <button key={st.id} onClick={() => setI(n)} tabIndex={-1}
                className={`h-2 rounded-full transition-all ${n === i ? 'w-5 bg-blue-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
            ))}
          </div>
          {last ? (
            <button onClick={onClose} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Done</button>
          ) : (
            <button onClick={() => go(1)} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Next →</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaudeConnectGuide;
